// AssemblyAI Universal Streaming v3: https://www.assemblyai.com/docs/streaming/message-sequence
import type {
  BeginEvent,
  ErrorEvent,
  HeartbeatEvent,
  SilenceEvent,
  SpeakerRevisionEvent,
  SpeakerRevisionItem,
  SpeechStartedEvent,
  StreamingWord,
  TerminationEvent,
  TurnEvent,
  WarningEvent,
} from "assemblyai/streaming";

import {
  type SpeakerLiveTranscriptTurn,
  type SpeakerWord,
  replaceTurn,
  turnKey,
} from "./segments";

// SDK used for types only: its runtime can't set
// `speaker_labels_revision_interval_ms`, so we keep our own socket.

export type AaiWord = StreamingWord & { speaker_confidence?: number };

export type AaiBegin = BeginEvent & {
  configuration?: { model?: string; mode?: string; api_version?: string };
};

export type AaiTurn = Omit<TurnEvent, "words"> & {
  words?: AaiWord[];
  speaker_confidence?: number;
};

export type AaiSpeakerRevision = SpeakerRevisionEvent;

export type AaiServerMessage =
  | AaiBegin
  | SpeechStartedEvent
  | AaiTurn
  | AaiSpeakerRevision
  | HeartbeatEvent
  | SilenceEvent
  | WarningEvent
  | TerminationEvent
  | ErrorEvent;

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

// Docs show SpeakerRevision both as `revisions[]` and a flat single-turn
// shape; both are normalised to `revisions[]`.
export function parseServerMessage(raw: unknown): AaiServerMessage | null {
  let data: unknown;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  } else {
    data = raw;
  }
  if (!isObject(data) || typeof data.type !== "string") return null;

  switch (data.type) {
    case "Begin":
      return typeof data.id === "string" ? (data as AaiBegin) : null;
    case "SpeechStarted":
    case "Heartbeat":
    case "Silence":
    case "Warning":
    case "Termination":
    case "Error":
      return data as AaiServerMessage;
    case "Turn":
      return typeof data.turn_order === "number" &&
        typeof data.transcript === "string"
        ? ({
            ...data,
            end_of_turn: data.end_of_turn === true,
            turn_is_formatted: data.turn_is_formatted === true,
          } as AaiTurn)
        : null;
    case "SpeakerRevision": {
      if (Array.isArray(data.revisions)) {
        const revisions = data.revisions
          .filter(
            (r): r is SpeakerRevisionItem =>
              isObject(r) && typeof r.turn_order === "number"
          )
          .map((r) => ({ ...r, words: Array.isArray(r.words) ? r.words : [] }));
        return { type: "SpeakerRevision", revisions };
      }
      if (typeof data.turn_order === "number") {
        const entry = data as unknown as SpeakerRevisionItem;
        return {
          type: "SpeakerRevision",
          revisions: [
            { ...entry, words: Array.isArray(entry.words) ? entry.words : [] },
          ],
        };
      }
      return null;
    }
    default:
      return null;
  }
}

// An end-of-turn but unformatted Turn is superseded by its formatted twin.
export function isFinalTurn(turn: AaiTurn): boolean {
  return turn.end_of_turn && turn.turn_is_formatted;
}

// "PENDING"/"UNKNOWN" mean unattributed audio, not a new speaker.
export function normalizeSpeaker(label: unknown): string | undefined {
  if (typeof label !== "string") return undefined;
  const trimmed = label.trim();
  if (!trimmed) return undefined;
  const upper = trimmed.toUpperCase();
  if (upper === "PENDING" || upper === "UNKNOWN") return undefined;
  return trimmed;
}

// `speaker_confidence` of 0 means "unavailable", not "wrong".
export function normalizeSpeakerConfidence(value: unknown): number | undefined {
  return typeof value === "number" && value > 0 ? value : undefined;
}

// Word ms are relative to this connection's first frame; `offsetSec` maps
// them onto the recording timeline.
export function toSpeakerWords(
  words: AaiWord[] | undefined,
  offsetSec: number
): SpeakerWord[] {
  if (!words) return [];
  return words
    .filter((w) => typeof w.text === "string" && w.text.trim() !== "")
    .map((w) => {
      const speaker = normalizeSpeaker(w.speaker);
      return {
        text: w.text,
        startSec: offsetSec + (Number(w.start) || 0) / 1000,
        endSec: offsetSec + (Number(w.end) || 0) / 1000,
        ...(speaker ? { speaker } : {}),
      };
    });
}

// `turn_order` restarts per socket, so revisions are scoped to `connectionId`.
export function applySpeakerRevision(
  turns: SpeakerLiveTranscriptTurn[],
  connectionId: string,
  revision: AaiSpeakerRevision
): SpeakerLiveTranscriptTurn[] {
  let next = turns;
  for (const entry of revision.revisions) {
    next = replaceTurn(next, turnKey(connectionId, entry.turn_order), (turn) => {
      const speaker = normalizeSpeaker(entry.speaker_label);
      const revisedWords = entry.words ?? [];
      const words = turn.words?.map((word, i) => {
        const revised = revisedWords[i];
        if (!revised) return word;
        const wordSpeaker = normalizeSpeaker(revised.speaker);
        const { speaker: _old, ...rest } = word;
        return wordSpeaker ? { ...rest, speaker: wordSpeaker } : rest;
      });
      const {
        speaker: _oldSpeaker,
        speakerConfidence: _oldConfidence,
        ...rest
      } = turn;
      return {
        ...rest,
        ...(speaker ? { speaker } : {}),
        ...(words ? { words } : {}),
      };
    });
  }
  return next;
}

export type CloseDisposition = "normal" | "reconnect" | "fatal";

// Only auth failure (1008) and the retired v2 endpoint are non-retryable;
// everything else gets a bounded reconnect with a fresh token.
const FATAL_CODES = new Set([1008, 410]);

// The server sends an Error message then closes, so check its code too.
export function classifyClose(
  closeCode: number,
  lastErrorCode: number | undefined,
  terminating: boolean
): CloseDisposition {
  if (
    FATAL_CODES.has(closeCode) ||
    (lastErrorCode !== undefined && FATAL_CODES.has(lastErrorCode))
  ) {
    return "fatal";
  }
  if (terminating) return "normal";
  return "reconnect";
}
